/**
 * TipTap extension that adds a `lineSpacing` attribute to paragraphs
 * and headings, allowing Google-Docs-style line height control.
 *
 * Supported values: "1", "1.15", "1.5", "2", or any numeric string.
 * The value is rendered as an inline `line-height` style.
 */

import { Extension } from "@tiptap/core";

export interface LineSpacingOptions {
  types: string[];
  defaultSpacing: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lineSpacing: {
      setLineSpacing: (spacing: string) => ReturnType;
      unsetLineSpacing: () => ReturnType;
    };
  }
}

export const LineSpacing = Extension.create<LineSpacingOptions>({
  name: "lineSpacing",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      defaultSpacing: "1.15",
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineSpacing: {
            default: null,
            parseHTML: (element) => {
              const lh = element.style.lineHeight;
              if (!lh) return null;
              const num = parseFloat(lh);
              if (!isNaN(num) && num > 0) return String(num);
              return null;
            },
            renderHTML: (attributes) => {
              if (!attributes.lineSpacing) return {};
              return {
                style: `line-height: ${attributes.lineSpacing}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineSpacing:
        (spacing) =>
        ({ commands }) => {
          return this.options.types.every((type) =>
            commands.updateAttributes(type, { lineSpacing: spacing }),
          );
        },
      unsetLineSpacing:
        () =>
        ({ commands }) => {
          return this.options.types.every((type) =>
            commands.resetAttributes(type, "lineSpacing"),
          );
        },
    };
  },
});
